import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import TekyuLogo from './TekyuLogo';
import { buildBootcampUrl } from './utils/leadCapture';

const RolesPage = () => {
  const [willingToBeTechnical, setWillingToBeTechnical] = useState(null);
  const [wantsConfidentRole, setWantsConfidentRole] = useState(null);
  const [salaryRange, setSalaryRange] = useState('');
  const navigate = useNavigate();
  const bootcampUrl = buildBootcampUrl({ utmMedium: 'roles', utmCampaign: 'bootcamp' });

  const roles = [
    { 
      title: 'DevSecOps', 
      path: '/devsecops', 
      technical: true, 
      confident: true, 
      minSalary: 70000, 
      highlightTechnical: true, 
      highlightNonTechnical: true,
      description: 'Integrate security practices into DevOps workflows. Perfect for those interested in secure software delivery and automation.'
    },
    { 
      title: 'DevSecAI', 
      path: '/devsecai', 
      technical: true, 
      confident: true, 
      minSalary: 75000, 
      highlightTechnical: true, 
      highlightNonTechnical: false,
      description: 'Combine AI security with MLOps practices. Ideal for those interested in securing machine learning systems and AI operations.'
    },
    { 
      title: 'DevEx', 
      path: '/devex', 
      technical: true, 
      confident: true, 
      minSalary: 65000, 
      highlightTechnical: true, 
      highlightNonTechnical: false,
      description: 'Focus on developer experience and productivity tools. Perfect for those interested in improving development workflows.'
    },
    { 
      title: 'Cloud/Platform Engineer', 
      path: '/cloud-platform-engineer', 
      technical: true, 
      confident: false, 
      minSalary: 75000, 
      highlightTechnical: true, 
      highlightNonTechnical: false,
      description: 'Specialise in cloud infrastructure and platform services. Ideal for those interested in cloud architecture and scalable systems.'
    },
    { 
      title: 'DevOps', 
      path: '/devops-questionnaire', 
      technical: true, 
      confident: true, 
      minSalary: 70000, 
      highlightTechnical: true, 
      highlightNonTechnical: false,
      description: 'Build and maintain CI/CD pipelines and infrastructure. Perfect for those interested in automation and deployment.'
    },
    { 
      title: 'SRE', 
      path: '/sre-questionnaire', 
      technical: true, 
      confident: false, 
      minSalary: 80000, 
      highlightTechnical: true, 
      highlightNonTechnical: true,
      description: 'Focus on system reliability and scalability. Ideal for those interested in maintaining large-scale systems.'
    },
    { 
      title: 'Penetration Testing', 
      path: '/penetration-testing', 
      technical: true, 
      confident: false, 
      minSalary: 65000, 
      highlightTechnical: true, 
      highlightNonTechnical: false,
      description: 'Identify and exploit security vulnerabilities. Perfect for those interested in offensive security.'
    },
    { 
      title: 'CyberGRC', 
      path: '/cybergrc', 
      technical: false, 
      confident: true, 
      minSalary: 60000, 
      highlightTechnical: false, 
      highlightNonTechnical: true,
      description: 'Manage governance, risk, and compliance in cybersecurity. Ideal for those interested in security policy and frameworks.'
    },
    { 
      title: 'Product Manager', 
      path: '/product-manager', 
      technical: false, 
      confident: true, 
      minSalary: 70000, 
      highlightTechnical: false, 
      highlightNonTechnical: true,
      description: 'Lead product development and strategy. Perfect for those who want to bridge business and technology.'
    },
    { 
      title: 'AI/Data Engineer', 
      path: '/ai-data-engineer', 
      technical: true, 
      confident: false, 
      minSalary: 80000, 
      highlightTechnical: true, 
      highlightNonTechnical: false,
      description: 'Build and maintain AI and data infrastructure. Ideal for those interested in machine learning and data systems.'
    },
    { 
      title: 'Security Analyst', 
      path: '/security-analyst', 
      technical: true, 
      confident: true, 
      minSalary: 65000, 
      highlightTechnical: true, 
      highlightNonTechnical: true,
      description: 'Monitor and respond to security threats. Perfect for those interested in defensive security operations.'
    },
    { 
      title: 'Full-Stack Engineer', 
      path: '/full-stack-engineer', 
      technical: true, 
      confident: true, 
      minSalary: 70000, 
      highlightTechnical: true, 
      highlightNonTechnical: false,
      description: 'Develop both frontend and backend applications. Ideal for those who want to build complete web applications.'
    },
  ];

  const isRoleSuitable = (role) => {
    if (willingToBeTechnical === null && wantsConfidentRole === null && salaryRange === '') {
      return false;
    }

    if ((role.title === 'DevSecOps' || role.title === 'DevEx' || role.title === 'DevSecAI') && 
        ((willingToBeTechnical === false && wantsConfidentRole === false) ||
         (willingToBeTechnical === true && wantsConfidentRole === false) ||
         (willingToBeTechnical === false && wantsConfidentRole === true))) {
      return true;
    }

    if (willingToBeTechnical !== null) {
      if (willingToBeTechnical && !role.highlightTechnical) return false;
      if (!willingToBeTechnical && !role.highlightNonTechnical) return false;
    }
    if (wantsConfidentRole !== null && role.confident !== wantsConfidentRole) return false;
    
    if (salaryRange === '46k-99k' && ['Cyber GRC', 'Security Analyst'].includes(role.title)) return false;
    if (salaryRange === '100k+' && !['DevSecOps', 'DevSecAI', 'DevEx', 'Cloud/Platform Engineer', 'DevOps', 'SRE', 'AI/Data Engineer'].includes(role.title)) return false;
    return true;
  };

  const handleRoleClick = (path) => {
    if (path === '/devops-questionnaire' || path === '/sre-questionnaire' || 
        path === '/product-manager' || path === '/ai-data-engineer' || 
        path === '/full-stack-engineer') {
      navigate(path);
    }
  };

  return (
    <div className="min-h-screen app-shell app-ambient">
      <div className="app-content mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
        {/* Header */}
        <div className="text-center mb-10">
          <Link to="/dashboard">
            <TekyuLogo ariaLabel="Tekyu - Tech Career Planning Platform" className="h-20 sm:h-24 w-auto mx-auto mb-6 drop-shadow-[0_12px_24px_rgba(0,0,0,0.35)]" />
          </Link>
          <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight">
            Choose Your <span className="text-[color:var(--primary)]">Career Path</span>
          </h1>
          <p className="mt-4 text-lg sm:text-xl text-[color:var(--muted)] max-w-3xl mx-auto">
            Select a role to get your personalized learning roadmap with certifications and progress tracking.
          </p>
        </div>

        {/* Role Suitability Filter */}
        <div className="backdrop-blur p-6 rounded-2xl border mb-10 max-w-3xl mx-auto bg-[color:var(--surface)] border-[color:var(--border)]">
          <h3 className="text-2xl font-bold text-[color:var(--primary)] mb-4">Find Your Perfect Role</h3>
          <div className="space-y-4">
            <div>
              <p className="text-[color:var(--fg)] mb-2">Are You Willing To Become More Technical?</p>
              <div className="flex space-x-4">
                <button 
                  onClick={() => setWillingToBeTechnical(true)} 
                  className={`px-4 py-2 rounded-xl border ${willingToBeTechnical === true ? 'bg-[color:var(--primary)] text-slate-950 border-transparent' : 'bg-[color:var(--surface-2)] text-[color:var(--fg)] border-[color:var(--border)] hover:opacity-95'}`}
                >
                  Yes
                </button>
                <button 
                  onClick={() => setWillingToBeTechnical(false)} 
                  className={`px-4 py-2 rounded-xl border ${willingToBeTechnical === false ? 'bg-[color:var(--primary)] text-slate-950 border-transparent' : 'bg-[color:var(--surface-2)] text-[color:var(--fg)] border-[color:var(--border)] hover:opacity-95'}`}
                >
                  No
                </button>
              </div>
            </div>
            <div>
              <p className="text-[color:var(--fg)] mb-2">Do You Want A Role That Requires Confidence And Talking?</p>
              <div className="flex space-x-4">
                <button 
                  onClick={() => setWantsConfidentRole(true)} 
                  className={`px-4 py-2 rounded-xl border ${wantsConfidentRole === true ? 'bg-[color:var(--primary)] text-slate-950 border-transparent' : 'bg-[color:var(--surface-2)] text-[color:var(--fg)] border-[color:var(--border)] hover:opacity-95'}`}
                >
                  Yes
                </button>
                <button 
                  onClick={() => setWantsConfidentRole(false)} 
                  className={`px-4 py-2 rounded-xl border ${wantsConfidentRole === false ? 'bg-[color:var(--primary)] text-slate-950 border-transparent' : 'bg-[color:var(--surface-2)] text-[color:var(--fg)] border-[color:var(--border)] hover:opacity-95'}`}
                >
                  No
                </button>
              </div>
            </div>
            <div>
              <p className="text-[color:var(--fg)] mb-2">How Much Do You Want To Earn Within 18 Months? (£)</p>
              <select
                value={salaryRange}
                onChange={(e) => setSalaryRange(e.target.value)}
                className="w-full px-4 py-2 rounded-xl transition-colors bg-[color:var(--surface-2)] border border-[color:var(--border)] text-[color:var(--fg)] focus:ring-1 focus:ring-[color:var(--primary)]"
              >
                <option value="">Select Salary Range</option>
                <option value="0-45k">£0 - £45,000</option>
                <option value="46k-99k">£46,000 - £99,000</option>
                <option value="100k+">£100,000+</option>
              </select>
            </div>
          </div>
          {(willingToBeTechnical !== null || wantsConfidentRole !== null || salaryRange !== '') && (
            <div className="mt-4 text-sm text-[color:var(--primary)]">
              • Recommended paths are highlighted in green
            </div>
          )}
        </div>

        {/* Roles Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl w-full mb-12 mx-auto">
          {roles.map((role, index) => (
            <div 
              key={index} 
              className={`p-6 rounded-lg border transition-all ${
                isRoleSuitable(role) 
                  ? 'border-green-500/50 bg-[color:var(--surface)] shadow-[0_0_0_1px_rgba(74,222,128,0.2)] hover:shadow-[0_0_20px_rgba(74,222,128,0.3)]'
                  : 'bg-[color:var(--surface-2)] border-[color:var(--border)] hover:opacity-95 hover:border-green-400/30'
              }`}
            >
              {role.title === 'DevOps' || role.title === 'SRE' || role.title === 'Product Manager' || 
               role.title === 'AI/Data Engineer' || role.title === 'Full-Stack Engineer' ? (
                <div 
                  onClick={() => handleRoleClick(role.path)} 
                  className="block text-center cursor-pointer"
                >
                  <h3 className="text-2xl font-bold text-[color:var(--primary)] mb-2">{role.title}</h3>
                  <p className="text-[color:var(--muted)] text-sm">{role.description}</p>
                </div>
              ) : (
                <Link to={role.path} className="block text-center">
                  <h3 className="text-2xl font-bold text-[color:var(--primary)] mb-2">{role.title}</h3>
                  <p className="text-[color:var(--muted)] text-sm">{role.description}</p>
                </Link>
              )}
            </div>
          ))}
        </div>

        {/* Bootcamp CTA */}
        <div className="max-w-4xl mx-auto mb-12">
          <div className="rounded-2xl border-2 border-indigo-500/30 bg-gradient-to-br from-indigo-950/40 to-slate-900/60 p-8 text-center">
            <h3 className="text-2xl font-extrabold text-white mb-3">
              Want Structured Guidance from Industry Leaders?
            </h3>
            <p className="text-slate-300 mb-6 max-w-2xl mx-auto">
              Join the Cyber Agoge Bootcamp for live teaching, guided practicals and project support.
            </p>
            <a
              href={bootcampUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center rounded-xl px-8 py-4 font-bold transition-all bg-gradient-to-r from-indigo-500 to-purple-500 text-white hover:opacity-90 shadow-lg shadow-indigo-500/25"
            >
              Learn More About Cyber Agoge Bootcamp
            </a>
          </div>
        </div>

        {/* Back to Home */}
        <div className="text-center">
          <Link
            to="/dashboard"
            className="inline-flex items-center gap-2 text-[color:var(--muted)] hover:text-white transition-colors"
          >
            <span>←</span>
            Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default RolesPage;
